import React, { useRef, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CircularProgress from "@mui/material/CircularProgress";
import WarningIcon from "@mui/icons-material/Warning";
import { CAMERA_ERRORS, LANGUAGE } from "@/constants";
import Typography from "@mui/material/Typography";
import Traductor from "@utils/traductor";
import theme from "@/theme";

const traductor = new Traductor(LANGUAGE);

const constraints = {
  video: {
    facingMode: "environment",
  },
};

const getErrorMessage = (error) => {
  const isCameraNotAvailable = CAMERA_ERRORS.NOT_AVAILABLE === error;
  const isCameraNotAllowed = CAMERA_ERRORS.NOT_ALLOWED === error;
  if (isCameraNotAvailable) {
    return traductor.translate("cameraNotAvailable");
  }
  if (isCameraNotAllowed) {
    return traductor.translate("cameraAccessDenied");
  }
  return traductor.translate("cameraAccessError");
};

const getWebcamStream = () => {
  if (navigator.mediaDevices) {
    return navigator.mediaDevices.getUserMedia(constraints);
  }
  return Promise.reject(new Error(CAMERA_ERRORS.NOT_AVAILABLE));
};

export const Camera = (props = {}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [streamError, setStreamError] = useState(null);
  const [file, setFile] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStream = useRef(null);

  const stopWebCamStream = () => {
    if (cameraStream.current) {
      cameraStream.current.getTracks().forEach((track) => track.stop());
    }
  };

  const startWebCamStream = () => {
    const handleWebCamStreamSuccess = (stream) => {
      setIsLoading(false);
      if ("srcObject" in videoRef.current) {
        videoRef.current.srcObject = stream;
      } else {
        videoRef.current.src = URL.createObjectURL(stream);
      }
      cameraStream.current = stream;
      videoRef.current.play();
    };

    const handleWebCamStreamFailed = (error) => {
      setIsLoading(false);
      setStreamError(error.name);
    };

    getWebcamStream()
      .then(handleWebCamStreamSuccess)
      .catch(handleWebCamStreamFailed);
  };

  useEffect(() => {
    startWebCamStream();
    return () => stopWebCamStream();
  }, []);

  const handleTakePicture = () => {
    if (file) {
      startWebCamStream();
      videoRef.current.play();
      setFile(null);
      props.onTakepicture(null);
      return;
    }
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const ctx = canvas.getContext("2d");
    const [videoTrack] = videoRef.current.srcObject.getVideoTracks();
    const settings = videoTrack.getSettings();
    const { width, height } = settings;
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(video, 0, 0, width, height);
    video.pause();
    canvasRef.current.toBlob((blob) => {
      setFile(blob);
      props.onTakepicture(blob);
    });
    stopWebCamStream();
  };

  return (
    <Box>
      {isLoading && (
        <Box
          sx={{ display: "flex", paddingTop: "100px", paddingBottom: "100px" }}
        >
          <CircularProgress sx={{ margin: "0px auto" }} />
        </Box>
      )}

      {streamError && !isLoading && (
        <Box
          sx={{
            display: "block",
            paddingTop: "50px",
            paddingBottom: "50px",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.dashbaord.header,
              textAlign: "center",
              fontSize: "12px",
            }}
            variant="button"
            display="block"
            gutterBottom
          >
            <>
              <WarningIcon
                sx={{
                  color: "orange",
                  display: "block",
                  textAlign: "center",
                  margin: "0px auto",
                  marginBottom: "5px",
                  padding: "4px",
                  fontSize: "60px",
                }}
              />
              {getErrorMessage(streamError)}
            </>
          </Typography>
        </Box>
      )}

      {!streamError && !isLoading && (
        <Box
          sx={{
            position: "relative",
            top: "-5px",
          }}
        >
          <video
            width="100%"
            height="500"
            loop
            muted
            playsInline
            ref={videoRef}
            style={{
              backgroundColor: "black",
              objectFit: "contain",
            }}
          />
          <Box
            sx={{
              overflow: "hidden",
              width: "100%",
              height: "100%",
              position: "absolute",
              left: "0px",
              top: "0px",
            }}
          >
            <canvas
              ref={canvasRef}
              style={{
                visibility: "hidden",
              }}
            />
          </Box>
          <Button
            onClick={handleTakePicture}
            variant="contained"
            sx={{
              width: "100%",
              backgroundColor: props.color,
              borderRadius: 0,
              marginTop: "10px",
              height: "50px",
            }}
            endIcon={<CameraAltIcon />}
          >
            {file ? props.labels.repeatPhoto : props.labels.takePicture}
          </Button>
        </Box>
      )}
    </Box>
  );
};
